/**
 * Species- and culture-based name generator (no real people, no titles, no gendered forms, no single language).
 * Input: species + culture + a deterministic rng; output: a structured list of name parts and a display form.
 * Names are proper nouns and are never translated. The syllable tables are invented, not real words.
 */
export const SPECIES_IDS = ['human', 'vorr', 'selu'] as const;
export type SpeciesId = (typeof SPECIES_IDS)[number];
export const DEFAULT_SPECIES: SpeciesId = 'human';

interface Culture {
  /** Syllable pools per position class. */
  onset: string[]; vowel: string[]; coda: string[];
  /** Name structure: how many parts and how many syllables each. */
  parts: Array<[number, number]>;
  join: string;
}

const C = (onset: string, vowel: string, coda: string, parts: Array<[number, number]>, join = ' '): Culture =>
  ({ onset: onset.split(' '), vowel: vowel.split(' '), coda: coda.split(' '), parts, join });

/** Cultures per species (ids are stable; their display names live in the catalog). */
export const CULTURES: Record<SpeciesId, Record<string, Culture>> = {
  human: {
    nordic: C('b d f g h k l m n r s t v', 'a e i o u ei', ' n r s l d', [[2, 3], [2, 3]]),
    sahel: C('b d j k l m n s t y z', 'a e i o u aa', ' n m l r', [[2, 3], [2, 3]]),
    andean: C('ch k l m n p q r s t w y', 'a i u o', ' n s q', [[2, 3], [2, 3]]),
    steppe: C('b g k m n s t z', 'a e i o u ü', ' n r l g', [[2, 3], [1, 2]]),
    littoral: C('c d f g l m p r s t v', 'a e i o u ia', ' n s l', [[2, 3], [2, 3]]),
    highland: C('d g k l m n r s t', 'a e i o u ou', ' n r s', [[1, 2], [2, 3]], ' '),
  },
  // Invented species: hard, clan-like names
  vorr: {
    karr: C('k g t d z v', 'a o u', 'r k g th x', [[1, 2], [1, 2]], '-'),
    drem: C('dr gr kr br t', 'a o e', 'k rn x zz', [[1, 2], [1, 1]], '-'),
  },
  // Invented species: soft, vowel-rich single names
  selu: {
    liu: C('l s f w n h', 'ae i ou ia e', ' n l', [[2, 3]]),
    oshe: C('sh h f s l', 'o a u oe ai', ' l', [[2, 4]]),
  },
};

export interface GeneratedName { parts: string[]; display: string }

export const culturesOf = (species: SpeciesId): string[] => Object.keys(CULTURES[species]);

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** `rng` returns [0,1): it decides everything, so the same inputs always give the same name. */
export function generateName(species: SpeciesId, culture: string, rng: () => number): GeneratedName {
  const c = CULTURES[species][culture] ?? Object.values(CULTURES[species])[0]!;
  const pick = (a: string[]) => a[Math.floor(rng() * a.length)]!;
  const parts = c.parts.map(([lo, hi]) => {
    const n = lo + Math.floor(rng() * (hi - lo + 1));
    let s = '';
    for (let i = 0; i < n; i++) s += pick(c.onset) + pick(c.vowel) + (i === n - 1 ? pick(c.coda) : '');
    return cap(s);
  });
  return { parts, display: parts.join(c.join) };
}
